import React, { useState, useEffect, useCallback } from "react";
import { ArrowLeft } from "lucide-react";
import { doc, getDoc } from "firebase/firestore";
import { useTranslation } from "react-i18next";

import { db } from "../../firebaseConfig";
import { ClipLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

import { useParams } from "react-router-dom";
import ClassCard from "../../components/ClassCard";
import Modal from "react-modal";
import ExploreClassCard from "../../components/ExploreClassCard";
import PlansModal from "../../components/PlansModal";
import { useGroupJoining } from "../../hooks/useGroupJoining";
import EmptyState from "../../components/EmptyState";
import GroupInfoCard from "../../components/GroupInfoCard";
import ShowDescription from "../../components/ShowDescription";
Modal.setAppElement("#root");

const GroupDetailsNotJoinedUser = ({ onClose }) => {
  const { user, setUser } = useAuth(); // Destructure setUser to update context

  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasJoined, setHasJoined] = useState(false);
  const { t } = useTranslation();

  //------------------------------------------fetching groups-----------------------------------------//

  const { groupId } = useParams();
  const [group, setGroup] = useState(null);
  const [error, setError] = useState(null);
  const fetchGroup = useCallback(async () => {
    if (!groupId) {
      setError("No group ID provided");
      setLoading(false);
      return;
    }

    try {
      const groupDoc = await getDoc(doc(db, "groups", groupId));

      if (!groupDoc.exists()) {
        setError("Group not found");
        setLoading(false);
        return;
      }

      setGroup({ id: groupDoc.id, ...groupDoc.data() });
    } catch (err) {
      console.error("Error fetching group:", err);
      setError("Failed to fetch group details");
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    fetchGroup();
  }, [fetchGroup]);

  // Check if user is already a member and redirect if they are
  useEffect(() => {
    if (
      group &&
      user &&
      user.joinedGroups &&
      user.joinedGroups.includes(groupId)
    ) {
      // User is already a member, redirect to the joined group view
      navigate(`/groupDetailsUser/${groupId}`, { replace: true });
    }
  }, [group, user, groupId, navigate]);

  // Check if user has joined this group
  useEffect(() => {
    if (user && user.joinedGroups && user.joinedGroups.includes(groupId)) {
      setHasJoined(true);
    }
  }, [user, groupId]);

  //-----------------------------getting tutor details------------------------------------------//

  //---------------------------------------------------------------------------------------------------//

  const fetchData = useCallback(async () => {
    if (!group) return;

    try {
      // Fetch classes
      if (group.classIds && group.classIds.length > 0) {
        const classesData = await Promise.all(
          group.classIds.map(async (classId) => {
            const classDoc = await getDoc(doc(db, "classes", classId));
            return classDoc.exists()
              ? { id: classDoc.id, ...classDoc.data() }
              : null;
          }),
        );
        setClasses(classesData.filter(Boolean));
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  }, [group]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  //--------------------------------------------------adding a class--------------------------------------------//

  const renderClasses = () => {
    if (classes.length === 0) {
      return (
        <div className="flex h-96 items-center justify-center">
          <EmptyState message="No classes available" />
        </div>
      );
    }

    // Get enrolled classes from localStorage
    const user = JSON.parse(sessionStorage.getItem("user"));
    const enrolledClasses = user?.enrolledClasses || [];
    console.warn("enrolledClasses", classes);

    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3">
        {classes.map((classItem) => {
          const isEnrolled = enrolledClasses.includes(classItem.classId);

          return isEnrolled ? (
            <ClassCard
              key={classItem.classId}
              classId={classItem.classId}
              className={classItem.className}
              language={classItem.language}
              languageLevel={classItem.languageLevel}
              classDateTime={classItem.classDateTime}
              classDuration={classItem.classDuration}
              adminId={classItem.adminId}
              adminName={classItem.adminName}
              adminImageUrl={classItem.adminImageUrl}
              classMemberIds={classItem.classMemberIds}
              availableSpots={classItem.availableSpots}
              imageUrl={classItem.imageUrl}
              classDescription={classItem.classDescription}
              classAddress={classItem.classAddress}
              groupId={classItem.groupId}
              recurrenceType={classItem.recurrenceType}
              classType={classItem.classType}
              classLocation={classItem.classLocation}
            />
          ) : (
            <ExploreClassCard
              key={classItem.classId}
              classId={classItem.classId}
              className={classItem.className}
              language={classItem.language}
              languageLevel={classItem.languageLevel}
              classDateTime={classItem.classDateTime}
              classDuration={classItem.classDuration}
              adminId={classItem.adminId}
              adminName={classItem.adminName}
              adminImageUrl={classItem.adminImageUrl}
              classMemberIds={classItem.classMemberIds}
              availableSpots={classItem.availableSpots}
              imageUrl={classItem.imageUrl}
              classDescription={classItem.classDescription}
              classAddress={classItem.classAddress}
              groupId={classItem.groupId}
              recurrenceType={classItem.recurrenceType}
              classType={classItem.classType}
              classLocation={classItem.classLocation}
            />
          );
        })}
      </div>
    );
  };

  //---------------------------------joining group----------------------------//\

  const updateContextAndSession = (newGroupId) => {
    // Create updated user object with new group
    const updatedUser = {
      ...user,
      joinedGroups: [...(user.joinedGroups || []), newGroupId],
    };

    // Update context
    setUser(updatedUser);

    // Update session storage
    const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
    const updatedStoredUser = {
      ...storedUser,
      joinedGroups: [...(storedUser.joinedGroups || []), newGroupId],
    };
    sessionStorage.setItem("user", JSON.stringify(updatedStoredUser));

    // Update local state to reflect that user has joined
    setHasJoined(true);
  };

  const { joinGroup, isProcessing } = useGroupJoining();
  const [isPlansModalOpen, setIsPlansModalOpen] = useState(false);
  const [showJoinConfirmation, setShowJoinConfirmation] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleJoinClick = () => {
    setShowJoinConfirmation(true);
  };

  const handleJoinConfirm = async () => {
    // Prevent multiple clicks
    if (isProcessing) {
      return;
    }

    try {
      const success = await joinGroup(
        user,
        { groupId, isPremium: group.isPremium },
        updateContextAndSession,
        () => {
          // Success callback - show success modal first
          setShowJoinConfirmation(false);
          setShowSuccessModal(true);
        },
        (errorMessage) => {
          // Error callback
          if (
            errorMessage.includes("subscription") ||
            errorMessage.includes("plan")
          ) {
            setIsPlansModalOpen(true);
          }
        },
      );

      if (success) {
        // If successful, show success modal
        setShowJoinConfirmation(false);
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error("Error joining group:", error);
      // Handle error appropriately - show error message to user
    }
  };

  const handleSuccessConfirm = () => {
    setShowSuccessModal(false);
    // Navigate to the joined group view
    navigate(`/groupDetailsUser/${groupId}`, { replace: true });
  };

  const handleCancel = () => {
    setShowJoinConfirmation(false);
  };

  //-----------------------------------------------------------------------//

  if (loading) {
    return (
      <div className="flex h-[100vh] items-center justify-center">
        <div className="rounded-lg bg-white p-8">
          <ClipLoader color="#FFB800" size={40} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="rounded-lg bg-white p-8">
          <p className="mb-4 text-red-500">{error}</p>
          <button
            onClick={onClose}
            className="rounded bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (!group) {
    return null;
  }

  return (
    <>
      <div className="flex min-h-screen">
        <div className="m-6 flex flex-1 rounded-3xl border">
          <div className="mx-4 flex w-full flex-col rounded-3xl bg-white p-6">
            <div className="mb-6 flex items-center justify-between border-b pb-4">
              <div className="flex items-center gap-4">
                <button
                  className="rounded-full bg-gray-100 p-3"
                  onClick={() => navigate(-1)}
                  disabled={isProcessing}
                >
                  <ArrowLeft size="30" />
                </button>
                <h1 className="text-4xl font-semibold">
                  {t("group-details.title")}
                </h1>
              </div>
            </div>

            <div className="flex h-screen flex-1 gap-6 overflow-hidden">
              {/* Left sidebar */}
              <div
                className={`h-[90vh] w-1/4 shrink-0 rounded-3xl p-6 ${
                  group.isPremium ? "bg-[#e6fce8]" : "bg-[#ffffea]"
                }`}
              >
                {" "}
                <div className="flex h-full flex-col items-center justify-between text-center">
                  <div className="flex w-full max-w-sm flex-col items-center text-center">
                    <img
                      src={group.imageUrl}
                      alt={group.groupName}
                      className="mb-4 h-24 w-24 rounded-full object-cover md:h-32 md:w-32"
                    />
                    <h3 className="mb-2 text-xl font-semibold md:text-2xl">
                      {group.groupName}
                    </h3>
                    <div className="mb-2 flex items-center gap-2">
                      <div className="flex flex-row items-center space-x-1">
                        <img
                          src={
                            group.groupLearningLanguage === "English"
                              ? "/svgs/xs-us.svg"
                              : group.groupLearningLanguage === "Spanish"
                                ? "/svgs/xs-spain.svg"
                                : "/svgs/eng-spanish-xs.svg"
                          }
                          alt={
                            group.groupLearningLanguage === "English"
                              ? "US Flag"
                              : "Spain Flag"
                          }
                          className="h-5 w-5 sm:w-auto"
                        />
                        <span className="md:text-md text-sm">
                          {group.groupLearningLanguage}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-col items-center md:flex-row md:space-x-40">
                      <div className="mb-2 flex items-center gap-1 md:mb-4">
                        <img
                          src={group.groupAdminImageUrl}
                          alt="admin"
                          className="w-4 rounded-full md:w-5"
                        />
                        <span className="text-xs text-gray-800 md:text-sm">
                          {group.groupAdminName}{" "}
                          {t("group-details.admin-label")}
                        </span>
                      </div>
                      <div className="mb-2 flex items-center gap-1 md:mb-4">
                        <img
                          alt="bammbuu"
                          src="/svgs/users.svg"
                          className="w-4 md:w-5"
                        />
                        <span className="text-xs text-gray-800 md:text-sm">
                          {(() => {
                            // Handle all possible cases
                            if (!group.memberIds) return "0";
                            if (Array.isArray(group.memberIds))
                              return group.memberIds.length.toString();
                            if (typeof group.memberIds === "number")
                              return group.memberIds.toString();
                            return "0";
                          })()}{" "}
                        </span>
                      </div>
                    </div>

                    <div className="w-full px-2">
                      <ShowDescription
                        description={group.groupDescription}
                        maxHeight={120}
                      />
                    </div>
                  </div>

                  <div className="w-full space-y-4 overflow-hidden">
                    {group.isPremium ? <GroupInfoCard group={group} /> : <></>}
                  </div>
                </div>
              </div>

              {/* Main content */}
              <div className="scrollbar-hide h-[90vh] flex-1 overflow-y-auto">
                <div className="scrollbar-hide h-full overflow-y-auto">
                  {loading ? (
                    <div className="flex h-full items-center justify-center">
                      <ClipLoader color="#FFB800" size={40} />
                    </div>
                  ) : (
                    <div className="pr-4">{renderClasses()}</div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-2 flex flex-row items-center justify-between">
              <button
                className="rounded-full border border-black px-8 py-2 text-lg text-black"
                onClick={handleCancel}
                disabled={isProcessing}
              >
                {t("group-details.buttons.cancel")}
              </button>
              <button
                className={`rounded-full border border-black px-8 py-2 text-lg text-black ${
                  isProcessing || hasJoined
                    ? "cursor-not-allowed bg-gray-300"
                    : "bg-[#ffbf00] hover:bg-[#e6ac00]"
                }`}
                onClick={handleJoinClick}
                disabled={isProcessing || hasJoined}
              >
                {hasJoined
                  ? "Joined Successfully!"
                  : isProcessing
                    ? t("group-details.buttons.join.loading")
                    : t("group-details.buttons.join.default")}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Join Confirmation Modal */}
      <Modal
        isOpen={showJoinConfirmation}
        onRequestClose={handleCancel}
        className="z-50 mx-auto mt-40 max-w-sm rounded-3xl bg-white p-6 font-urbanist outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      >
        <div className="text-center">
          <h2 className="mb-4 text-xl font-semibold">
            {t("exploreGroupCard.confirmJoining.title")}
          </h2>
          <p className="mb-6 text-gray-600">
            {group?.classType === "Individual Premium"
              ? t(
                  "exploreGroupCard.confirmJoining.descriptionPremiumIndividual",
                )
              : t("exploreGroupCard.confirmJoining.description")}
          </p>
          <div className="flex flex-row gap-2">
            <button
              className="w-full rounded-full border border-gray-300 py-2 font-medium hover:bg-gray-50"
              onClick={handleCancel}
              disabled={isProcessing}
            >
              {t("exploreGroupCard.confirmJoining.cancel")}
            </button>
            <button
              className={`w-full rounded-full border border-[#042f0c] py-2 font-medium text-black ${
                isProcessing
                  ? "cursor-not-allowed bg-gray-300"
                  : "bg-[#14b82c] hover:bg-[#119924]"
              }`}
              onClick={handleJoinConfirm}
              disabled={isProcessing}
            >
              {isProcessing
                ? t("exploreGroupCard.confirmJoining.joining")
                : t("exploreGroupCard.confirmJoining.confirm")}
            </button>
          </div>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onRequestClose={handleSuccessConfirm}
        className="z-50 mx-auto mt-40 max-w-sm rounded-3xl bg-white p-6 font-urbanist outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      >
        <div className="text-center">
          <div className="mb-4 flex justify-center">
            <div className="rounded-full bg-green-100 p-3">
              <svg
                className="h-8 w-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>
          <h2 className="mb-4 text-xl font-semibold text-green-800">
            {t("group-details.success.title")}
          </h2>
          <p className="mb-6 text-gray-600">
            {t("group-details.success.description")}
          </p>
          <button
            className="w-full rounded-full border border-[#042f0c] bg-[#14b82c] py-2 font-medium text-black hover:bg-[#119924]"
            onClick={handleSuccessConfirm}
          >
            {t("group-details.success.continue")}
          </button>
        </div>
      </Modal>

      <PlansModal
        isOpen={isPlansModalOpen}
        onClose={() => setIsPlansModalOpen(false)}
      />
    </>
  );
};

export default GroupDetailsNotJoinedUser;
